import { useState, type FormEvent } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import type { SettingsSiteRow } from '../../types';
import { sitePalette as PALETTE } from '../../../planning/colors';
import { SITE_MODAL_LABELS as LABELS  } from '../../../../utils/labels';
import AppDialog from '../../../../components/AppDialog';
import { useModalHistory } from '../../../../utils/useModalHistory';
import ColorPicker from './ColorPicker';
import SpinnerButton from '../../../../components/SpinnerButton';

const ORG_SCOPE = '__org__';

function pickDefaultColor(activeSites: SettingsSiteRow[], projectId: string | null): string {
  const scoped = projectId
    ? activeSites.filter((s) => s.project_id === projectId)
    : activeSites.filter((s) => s.project_id === null);
  const used = scoped.map((s) => s.color);
  return [...PALETTE].find((c) => !used.includes(c)) ?? PALETTE[scoped.length % PALETTE.length];
}

interface SiteModalProps {
  site?: SettingsSiteRow;
  activeSites: SettingsSiteRow[];
  activeProjects: { id: string; name: string; year: number }[];
  onClose: () => void;
  onSave: (name: string, projectId: string | null, color: string) => Promise<boolean>;
}

export default function SiteModal({
  site,
  activeSites,
  activeProjects,
  onClose,
  onSave,
}: SiteModalProps) {
  useModalHistory(onClose);
  const [name, setName] = useState(site?.name ?? '');
  const [projectId, setProjectId] = useState<string>(site?.project_id ?? ORG_SCOPE);
  const [color, setColor] = useState(site?.color ?? pickDefaultColor(activeSites, null));
  const [nameError, setNameError] = useState('');
  const [saving, setSaving] = useState(false);

  const isCreate = !site;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError(LABELS.nameRequired);
      return;
    }
    setSaving(true);
    const resolvedProjectId = projectId === ORG_SCOPE ? null : projectId;
    const ok = await onSave(name.trim(), resolvedProjectId, color);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <AppDialog open onClose={onClose} maxWidth="xs" fullWidth title={isCreate ? LABELS.createTitle : LABELS.editTitle} confirmLabel={LABELS.save} confirmFormId="site-form" confirmDisabled={saving} confirmLoading={saving} testId="site-modal">
      <form id="site-form" onSubmit={handleSubmit} noValidate>

        <DialogContent className="flex flex-col gap-4">
          <TextField
            label={LABELS.nameLabel}
            value={name}
            onChange={(e) => { setName(e.target.value); if (nameError) setNameError(''); }}
            required
            autoFocus
            fullWidth
            className="mt-2"
            error={!!nameError}
            helperText={nameError || undefined}
          />
          {isCreate ? (
            <FormControl fullWidth>
              <InputLabel>{LABELS.scopeLabel}</InputLabel>
              <Select
                label={LABELS.scopeLabel}
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                inputProps={{ 'aria-label': LABELS.scopeLabel }}
              >
                <MenuItem value={ORG_SCOPE}>{LABELS.scopeOrg}</MenuItem>
                {activeProjects.map((p) => (
                  <MenuItem key={p.id} value={p.id} data-testid={`option-${p.id}`}>
                    {p.name} ({p.year})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <FormControl fullWidth disabled>
              <InputLabel>{LABELS.scopeLabel}</InputLabel>
              <Select label={LABELS.scopeLabel} value={projectId} inputProps={{ 'aria-label': LABELS.scopeLabel }}>
                <MenuItem value={ORG_SCOPE}>{LABELS.scopeOrg}</MenuItem>
                {activeProjects.map((p) => (
                  <MenuItem key={p.id} value={p.id} data-testid={`option-${p.id}`}>
                    {p.name} ({p.year})
                  </MenuItem>
                ))}
                {site?.project_id && !activeProjects.find((p) => p.id === site.project_id) && (
                  <MenuItem value={site.project_id}>
                    {site.project_name ? `${site.project_name} (${site.project_year})` : LABELS.unknownProject}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          )}
          <div>
            <p className="text-sm font-medium text-text-secondary mt-0 mb-2">{LABELS.colorLabel}</p>
            <ColorPicker value={color} onChange={setColor} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" data-testid="cancel-button">
            {LABELS.cancel}
          </Button>
          <SpinnerButton type="submit" variant="contained" loading={saving} data-testid="save-button">
            {LABELS.save}
          </SpinnerButton>
        </DialogActions>
      </form>
    </AppDialog>
  );
}
